import {
  Chip,
  alpha,
  Stack,
  Divider,
  Checkbox,
  ListItem,
  Typography,
  IconButton,
  ListItemText,
  ListItemIcon,
  ListItemButton
} from "@material-ui/core";
import { db } from "firebaseConfig";
import { Groups, Today } from "@material-ui/icons";
import { fDate, fDateTimeWithoutSecond, fTimeWithoutSecond } from "utils/formatTime";
import { preventBubbling } from "utils/listings/helper";
import { adjustTaskTimeForToday } from "utils/calendar/helper";
import useSettings from "hooks/useSettings";
import useAuth from "hooks/useAuth";
import useCalendar from "hooks/useCalendar";
import { useTranslation } from "react-i18next";
import { formatShortCurrency, getFullName } from "utils/Haim/utils";
import { getTimeStatusByDate } from "./CalendarFormTimeStatus";
import { ContactTableAvatar } from "../contacts/table/rows/ContactTableAvatar";
import { useModal } from "hooks/useModal";
import { CalendarEventDialog } from "components/__haim/Common/CalendarEventDialog";
import useOrgDirectory from "hooks/useOrgDirectory";
import { isAssignedByMe, isAssignedToMe } from "utils/calendar/officeTasks";
import type { EventTaskTab } from "./CalendarTasksTabs";
import type { CSSProperties } from "react";
import type { CalendarEvent, CalendarEventStatus } from "../../../@types/sharedSchema";

type CalendarTasksRowProps = {
  event: CalendarEvent;
  currentTab: EventTaskTab;
  inListingMatch?: boolean;
};

export function CalendarTasksRow({
  event,
  currentTab,
  inListingMatch
}: CalendarTasksRowProps): JSX.Element {
  const { user } = useAuth();

  const { t: translate } = useTranslation();

  const { themeDirection } = useSettings();

  const { open, openModal, closeModal } = useModal();

  const { createOrUpdateEvent, deleteEvent, updateEventTimeById, updateOfficeTask } = useCalendar();

  const { getMember } = useOrgDirectory();

  const { id, title, start, status, userId, allDay, eventType, attendees, backgroundColor } = event;

  const checked = status === "done";

  const isOwner = user.email?.toLowerCase() === userId?.toLowerCase();

  // A task the user put in a colleague's calendar: theirs to change too, through the server.
  const assignedByMe = isAssignedByMe(event, user.email);

  const isAuthorized = isOwner || assignedByMe;

  const nameOf = (email: string): string => getMember(email)?.displayName ?? email;

  const handleEditModalOpen = (): void => {
    if (!isAuthorized) return;

    openModal();
  };

  const handleToggleChecked = async (): Promise<void> => {
    const nextStatus: CalendarEventStatus = checked ? "pending" : "done";

    if (assignedByMe) {
      await updateOfficeTask(event, { status: nextStatus });
      return;
    }

    try {
      await db
        .collection("calendar-settings-v2")
        .doc(user.email)
        .collection("calendar-events")
        .doc(id)
        .update({ status: nextStatus });
    } catch (error) {
      console.error(error);
    }
  };

  const handlePostponeTask = async (): Promise<void> => {
    const { start: newStart, end: newEnd } = adjustTaskTimeForToday(event.start, event.end);

    if (assignedByMe) {
      await updateOfficeTask(event, { start: newStart, end: newEnd, allDay: Boolean(allDay) });
      return;
    }

    // Via the context rather than a raw Firestore write: it also notifies the
    // server, which is what mirrors the new time into Google Calendar. A raw
    // write would only reach Google on the next hourly reconcile.
    await updateEventTimeById(id, newStart, newEnd, Boolean(allDay));
  };

  const isRTL = themeDirection === "rtl";

  const isTaskEvent = eventType === "task";

  const postponeButtonVisibleOnThisWeekUpcomingTab =
    ["thisWeek", "upcoming"].includes(currentTab) && getTimeStatusByDate(start) !== "today";

  const showPostponeButton =
    isTaskEvent && (currentTab === "overdue" || postponeButtonVisibleOnThisWeekUpcomingTab);

  const firstAttendee = attendees?.[0];

  const firstAttendeeFullName = getFullName(firstAttendee?.firstName, firstAttendee?.lastName);

  const textStyle = checked ? checkedTaskStyle : undefined;

  let formattedStartDate: string | null = null;

  if (!allDay) {
    if (currentTab === "today") {
      formattedStartDate = fTimeWithoutSecond(start, isRTL);
    } else {
      formattedStartDate = fDateTimeWithoutSecond(start, isRTL);
    }
  } else if (currentTab !== "today") {
    formattedStartDate = fDate(start, isRTL);
  }

  let listingLabel = null;

  const shouldShowListingLabel = inListingMatch && event.listingsData?.length;

  if (shouldShowListingLabel) {
    const { price, address } = event.listingsData[0];

    const { street, neighborhood, city } = address;

    let addressPart: string[];

    if (street) addressPart = [street, city];
    else addressPart = [neighborhood, city];

    const parsedAddressPart = addressPart.filter(Boolean).join(", ");
    const parsedPricePart = `(${formatShortCurrency(price)})`;

    const fullLabel = `${parsedAddressPart} ${parsedPricePart}`;

    listingLabel = (
      <Stack gap={0.5} direction="row">
        <Chip size="small" label={fullLabel} />
        {event.listingsData?.length > 1 && (
          <Chip size="small" label={`+${event.listingsData?.length - 1}`} />
        )}
      </Stack>
    );
  }

  return (
    <>
      <CalendarEventDialog
        open={open}
        event={event}
        onClose={closeModal}
        deleteEvent={deleteEvent}
        createOrUpdateEvent={createOrUpdateEvent}
      />
      <ListItem
        disablePadding
        secondaryAction={
          showPostponeButton && (
            <Stack alignItems="center">
              <IconButton
                size="small"
                onClick={preventBubbling(handlePostponeTask)}
                disabled={!isAuthorized}
              >
                <Today />
              </IconButton>
              <Typography variant="caption" fontSize="0.75rem">
                {translate("calendarPage.control.toToday")}
              </Typography>
            </Stack>
          )
        }
        onClick={handleEditModalOpen}
      >
        <ListItemButton disableRipple disableTouchRipple disabled={!isAuthorized}>
          {isTaskEvent ? (
            <ListItemIcon>
              <Checkbox
                checked={checked}
                disabled={!isAuthorized}
                onClick={preventBubbling(handleToggleChecked)}
              />
            </ListItemIcon>
          ) : (
            <ListItemIcon>
              <Groups sx={{ mx: 1 }} />
            </ListItemIcon>
          )}
          <Divider
            sx={{
              mr: 2,
              ml: -1,
              my: 0.5,
              borderColor: backgroundColor,
              borderRadius: 1,
              borderRightWidth: 6
            }}
            flexItem
            orientation="vertical"
          />
          <ListItemText
            primary={
              <>
                <Typography display="inline" variant="body1" sx={textStyle}>
                  {title}
                </Typography>
                {!isOwner && (
                  <Typography
                    display="inline"
                    variant="body2"
                    sx={{
                      ...textStyle,
                      color: alpha("#000", 0.4)
                    }}
                  >
                    {" "}
                    {assignedByMe
                      ? `· ${translate("calendarPage.officeTasks.assignedToLine", { name: nameOf(userId) })}`
                      : `(${nameOf(userId)})`}
                  </Typography>
                )}
              </>
            }
            secondary={
              <Stack alignItems="start" gap={0.5}>
                {isAssignedToMe(event, user.email) && (
                  <Typography variant="caption" color="text.secondary">
                    {translate("calendarPage.officeTasks.assignedByLine", { name: nameOf(event.assignedByUserId) })}
                  </Typography>
                )}
                {formattedStartDate && (
                  <Typography variant="body2" sx={textStyle}>
                    {formattedStartDate}
                  </Typography>
                )}
                {firstAttendee && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <ContactTableAvatar
                      sx={{ fontSize: 12 }}
                      avatarSize={24}
                      callerFullName={firstAttendeeFullName}
                    />
                    <Stack sx={textStyle} direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2">{firstAttendeeFullName}</Typography>
                      {attendees.length > 1 && (
                        <Typography variant="body2" fontSize="0.75rem">
                          +{attendees.length - 1}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                )}
                {listingLabel}
              </Stack>
            }
          />
        </ListItemButton>
      </ListItem>
    </>
  );
}

export const checkedTaskStyle: CSSProperties = {
  color: alpha("#000", 0.4),
  textDecoration: "line-through"
};
